import React, { useMemo, useState } from 'react';
import Table from '../../components/Table';
import Button from '../../components/Button';
import PlusIcon from 'assets/images/icon/plus-white.png';
import Input from '../../components/Input';
import search from 'assets/images/icon/search-normal.png';
import ToggleSwitch from '../../components/ToggleSwitch';
import Card from '../../components/Card';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import PersonIcon from 'assets/images/icon/person-icon';
import PersonsIcon from 'assets/images/icon/persons-icon';
import UserChecked from 'assets/images/icon/user-checked';
import UserDisabled from 'assets/images/icon/user-disabled';
import { stringMerger } from 'utils';
import { useQuery } from '@tanstack/react-query';
import { getEmployees } from 'services/employees';
import axiosInstance from '../../axios/axiosInstance';

const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

function SendInviteModal({ onClose }) {
    const [emails, setEmails]           = useState('');
    const [type, setType]               = useState('employee');
    const [sending, setSending]         = useState(false);
    const [results, setResults]         = useState(null);
    const [showRoleDetails, setShowRoleDetails] = useState(false);
    const [jobTitle, setJobTitle]       = useState('');
    const [department, setDepartment]   = useState('');
    const [employmentType, setEmploymentType] = useState('');
    const [startDate, setStartDate]     = useState('');
    const [locationId, setLocationId]   = useState(localStorage.getItem('locationId') || '');

    const companyId = localStorage.getItem('companyId');
    const lockedLocationId = localStorage.getItem('locationId') || '';
    const { data: locationsRaw = [] } = useQuery({
        queryKey: ['company-locations', companyId],
        queryFn: () => axiosInstance.get(`/companies/${companyId}/locations`).then(r => r.data),
        enabled: !!companyId,
        retry: false,
    });
    const locations = (Array.isArray(locationsRaw) ? locationsRaw : []).filter(l => l.isActive !== false);
    const requireLocation = locations.length > 0;

    const emailList = emails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    const emailCount = emailList.length;

    const handleSend = async () => {
        if (!emailCount) return;
        if (requireLocation && !String(locationId || '').trim()) {
            setResults({ error: 'Branch / Location is required before sending invites.' });
            return;
        }
        setSending(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const resp  = await axiosInstance.post(
                `${ONBOARDING_API}/api/invites/bulk-send`,
                {
                    emails: emailList,
                    type,
                    onboardingBaseUrl: 'https://onboarding.payrollnoot.com',
                    jobTitle:       jobTitle.trim()       || undefined,
                    department:     department.trim()     || undefined,
                    employmentType: employmentType        || undefined,
                    startDate:      startDate             || undefined,
                    locationId:     locationId.trim()     || undefined,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setResults(resp.data);
        } catch (err) {
            setResults({ error: err?.response?.data?.message || 'Failed to send invites.' });
        } finally {
            setSending(false);
        }
    };

    const pillBase  = { padding:'7px 20px', borderRadius:20, fontSize:13, fontWeight:600, cursor:'pointer', border:'2px solid', transition:'all 0.15s' };
    const inputSt   = { width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #e5e7eb', fontSize:13, outline:'none', boxSizing:'border-box' };
    const selectSt  = { ...inputSt, background:'#fff' };

    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#fff', borderRadius:20, padding:'36px 32px', maxWidth:560, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.22)', maxHeight:'90vh', overflowY:'auto' }}>

                {/* Header */}
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                        <div style={{ width:48, height:48, borderRadius:12, background:'#EFF6FF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                            ✉️
                        </div>
                        <div>
                            <h5 style={{ fontWeight:700, margin:0, fontSize:17, color:'#1a1a2e' }}>Send Onboarding Invites</h5>
                            <p style={{ color:'#888', fontSize:13, margin:'2px 0 0' }}>
                                Recipients get a unique invite link by email
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#aaa', cursor:'pointer', lineHeight:1, padding:0 }}>✕</button>
                </div>

                {!results ? (
                    <>
                        {/* Invite type — Employee only */}

                        {/* Email textarea */}
                        <div style={{ marginBottom:20 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                                <label style={{ fontWeight:600, fontSize:13, color:'#444' }}>Email addresses</label>
                                {emailCount > 0 && (
                                    <span style={{ background:'#1B75F0', color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:600 }}>
                                        {emailCount} recipient{emailCount !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <textarea
                                rows={4}
                                className="form-control"
                                placeholder={"john@example.com\njane@example.com\nbob@example.com"}
                                value={emails}
                                onChange={e => setEmails(e.target.value)}
                                style={{ fontSize:13, resize:'vertical', borderRadius:10, border:'1.5px solid #e5e7eb', padding:'10px 12px' }}
                            />
                            <p style={{ fontSize:12, color:'#aaa', marginTop:5 }}>One per line, or comma/semicolon-separated</p>
                        </div>

                        {requireLocation && (
                            <div style={{ marginBottom:20 }}>
                                <label style={{ display:'block', fontWeight:600, fontSize:13, color:'#444', marginBottom:6 }}>
                                    Branch / Location *
                                </label>
                                <select
                                    value={locationId}
                                    onChange={e => setLocationId(e.target.value)}
                                    disabled={!!lockedLocationId}
                                    style={selectSt}
                                >
                                    <option value="">Select branch / location…</option>
                                    {locations.map(l => (
                                        <option key={l.id} value={l.id}>{l.name}</option>
                                    ))}
                                </select>
                                <p style={{ fontSize:12, color:'#888', marginTop:5, marginBottom:0 }}>
                                    Assigned to the invite and applied when they finish self-onboarding.
                                </p>
                            </div>
                        )}

                        {/* Optional role details (collapsible) */}
                        <div style={{ marginBottom:20, border:'1.5px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
                            <button
                                type="button"
                                onClick={() => setShowRoleDetails(v => !v)}
                                style={{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', padding:'11px 16px', background:'#f9fafb', border:'none', cursor:'pointer', fontSize:13, fontWeight:600, color:'#444' }}
                            >
                                <span>📋 Pre-fill role details <span style={{ fontWeight:400, color:'#999' }}>(optional — great for individual invites)</span></span>
                                <span style={{ fontSize:16, color:'#aaa', transition:'transform 0.2s', display:'inline-block', transform: showRoleDetails ? 'rotate(180deg)' : 'none' }}>▾</span>
                            </button>
                            {showRoleDetails && (
                                <div style={{ padding:'16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                                    <div>
                                        <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Job Title</label>
                                        <input type="text" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Office Manager" style={inputSt} />
                                    </div>
                                    <div>
                                        <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Department</label>
                                        <input type="text" value={department} onChange={e => setDepartment(e.target.value)} placeholder="Operations" style={inputSt} />
                                    </div>
                                    <div>
                                        <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Employment Type</label>
                                        <select value={employmentType} onChange={e => setEmploymentType(e.target.value)} style={selectSt}>
                                            <option value="">Select type</option>
                                            <option value="Full-time">Full-time</option>
                                            <option value="Part-time">Part-time</option>
                                            <option value="Contract">Contract</option>
                                            <option value="Temporary">Temporary</option>
                                            <option value="Seasonal">Seasonal</option>
                                            <option value="Intern">Intern</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:5 }}>Start Date</label>
                                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputSt} />
                                    </div>
                                    <div style={{ gridColumn:'1/-1' }}>
                                        <p style={{ fontSize:12, color:'#888', margin:0, lineHeight:1.5 }}>
                                            These fields are stored with the invite and auto-populate the employee profile when they complete onboarding.
                                            Leave blank to let the employee fill in their own details, or fill in later from the employee profile.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Info banner */}
                        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', marginBottom:20, fontSize:13, color:'#166534', display:'flex', gap:8, alignItems:'flex-start' }}>
                            <span style={{ fontSize:15 }}>ℹ️</span>
                            <span>Each person receives a personalized email with their unique invite code and a one-click link to start onboarding.</span>
                        </div>

                        {/* Footer buttons */}
                        <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
                            <button
                                style={{ padding:'9px 22px', borderRadius:10, border:'1.5px solid #e5e7eb', background:'#fff', color:'#555', fontSize:14, fontWeight:600, cursor:'pointer' }}
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                style={{ padding:'9px 24px', borderRadius:10, border:'none', background: emailCount && !sending ? '#1B75F0' : '#93C5FD', color:'#fff', fontSize:14, fontWeight:700, cursor: emailCount && !sending ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', gap:8 }}
                                onClick={handleSend}
                                disabled={sending || !emailCount || (requireLocation && !locationId)}
                            >
                                {sending
                                    ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /> Sending…</>
                                    : `Send ${emailCount > 0 ? emailCount : ''} Invite${emailCount !== 1 ? 's' : ''} →`
                                }
                            </button>
                        </div>
                    </>

                ) : results.error ? (
                    <div>
                        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', color:'#991b1b', fontSize:14, marginBottom:16 }}>
                            ✗ {results.error}
                        </div>
                        <button style={{ padding:'8px 20px', borderRadius:10, border:'1.5px solid #e5e7eb', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }} onClick={() => setResults(null)}>← Try Again</button>
                    </div>

                ) : (
                    <div>
                        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'14px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
                            <span style={{ fontSize:22 }}>🎉</span>
                            <div>
                                <div style={{ fontWeight:700, color:'#166534', fontSize:15 }}>
                                    {results.sent} invite{results.sent !== 1 ? 's' : ''} sent!
                                </div>
                                <div style={{ fontSize:12, color:'#4ade80', marginTop:2 }}>Invite emails are on their way.</div>
                            </div>
                        </div>
                        <div style={{ maxHeight:220, overflowY:'auto', marginBottom:20, border:'1px solid #f0f0f0', borderRadius:10, overflow:'hidden' }}>
                            {results.results?.map((r, i) => (
                                <div key={i} style={{ fontSize:13, padding:'10px 14px', borderBottom:'1px solid #f5f5f5', display:'flex', justifyContent:'space-between', alignItems:'center', background: i%2===0 ? '#fff' : '#fafafa' }}>
                                    <div>
                                        <div style={{ fontWeight:500 }}>{r.email}</div>
                                        {r.token && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>Code: <code style={{ background:'#f3f4f6', padding:'1px 5px', borderRadius:4 }}>{r.token}</code></div>}
                                    </div>
                                    <span style={{ color: r.status==='sent'?'#22c55e':'#ef4444', fontWeight:700, fontSize:13 }}>
                                        {r.status==='sent' ? '✓ Sent' : '✗ Failed'}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <button
                            style={{ padding:'9px 24px', borderRadius:10, border:'none', background:'#1B75F0', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer' }}
                            onClick={onClose}
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const PAY_TYPE_BADGE = (payType) => {
    const pt = (payType || 'salary').toLowerCase();
    const isHourly = pt === 'hourly';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: isHourly ? '#e3f2fd' : '#f3e5f5',
            color:      isHourly ? '#1565c0' : '#6a1b9a',
            fontWeight: 600, fontSize: 11, padding: '3px 9px',
            borderRadius: 20, whiteSpace: 'nowrap',
        }}>
            {isHourly ? '⏱' : '💼'} {pt.charAt(0).toUpperCase() + pt.slice(1)}
        </span>
    );
};

const headers = [
    { headerName: "Code", key: "code" },
    { headerName: "Name", key: "name" },
    { headerName: "Email", key: "email" },
    { headerName: "Pay Type", key: "pay_type" },
    { headerName: "Active Status", key: "active_status" },
    { headerName: "Employment Status", key: "emp_status" },
    { headerName: "Onboard Status", key: "onboard_status" },
    { headerName: "I-9 Status", key: "i9_status" },
    { headerName: "", key: "action" },
];

function Employees() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm]       = useState('');
    const [showInactive, setShowInactive]   = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [activeFilter, setActiveFilter]   = useState(null);
    const { data: employeesRaw, isLoading } = useQuery({ queryKey: ['employees'], queryFn: getEmployees });
    const employees = React.useMemo(() => Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []), [employeesRaw]);

    const tableData = useMemo(() => employees
        .filter(e => e.onboardStatus !== 'pending_review')
        .filter(e => {
            if (activeFilter === 'terminated')   return !!e.terminationDate;
            if (activeFilter === 'active')       return e.active === true;
            if (activeFilter === 'setup_needed') return e.onboardStatus === 'completed' && (!e.payRate || Number(e.payRate) === 0);
            return showInactive ? e.active !== true : e.active === true;
        })
        .filter(e => !searchTerm || `${e.firstName} ${e.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) || (e.email||'').toLowerCase().includes(searchTerm.toLowerCase()))
        .map(e => {
            const fullName = `${e.firstName} ${e.lastName}`;
            // Show Setup Needed if employee completed self-onboarding but admin hasn't set pay rate yet
            const setupNeeded = e.onboardStatus === 'completed'
                && (!e.payRate || Number(e.payRate) === 0);
            return {
                id: e.id,
                code: e.id ? e.id.split('_')[0].toUpperCase() + '-' + e.id.slice(-6).toUpperCase() : '—',
                name: setupNeeded
                    ? <span style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                        {fullName}
                        <span style={{ background:'#051147', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, letterSpacing:'0.03em', whiteSpace:'nowrap' }}>
                            SETUP NEEDED
                        </span>
                      </span>
                    : fullName,
                email: e.email || '—',
                residence: [e.addressLine1, e.city, e.state].filter(Boolean).join(', ') || '—',
                pay_type: PAY_TYPE_BADGE(e.payType),
                active_status: e.active ? 'Active' : 'Inactive',
                emp_status: e.employmentType || e.status || '—',
                onboard_status: e.onboardStatus || '—',
                i9_status: e.i9Section2CompletedAt
                    ? <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 12 }}>✓ Complete</span>
                    : <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 12 }}>⚠ Missing</span>,
                termination_date: e.terminationDate ? new Date(e.terminationDate).toLocaleDateString() : '—',
            };
        }), [employees, searchTerm, showInactive, activeFilter]);

    const approvedEmployees = employees.filter(e => e.onboardStatus !== 'pending_review');
    const activeCount     = approvedEmployees.filter(e => !!e.active).length;
    const terminatedCount = approvedEmployees.filter(e => !!e.terminationDate).length;
    const blockingCount   = approvedEmployees.filter(e => e.onboardStatus === 'blocking').length;
    const attentionCount  = approvedEmployees.filter(e => e.onboardStatus === 'needs_attention' || e.onboardStatus === 'pending').length;
    const setupNeededCount = approvedEmployees.filter(e => e.onboardStatus === 'completed' && (!e.payRate || Number(e.payRate) === 0)).length;

    const handleCardClick = (filter) => {
        setActiveFilter(prev => prev === filter ? null : filter);
        setShowInactive(false);
    };

    const userDetail = [
        { title: 'Active Employees',        count: activeCount,        icon: <PersonIcon />,   filter: 'active' },
        { title: 'Setup Needed',            count: setupNeededCount,   icon: <UserChecked />,  filter: 'setup_needed' },
        { title: 'Onboarding Blocking',     count: blockingCount,      icon: <PersonsIcon />,  filter: null },
        { title: 'Terminated Employees',    count: terminatedCount,    icon: <UserDisabled />, filter: 'terminated' },
    ];

    const terminatedHeaders = [
        { headerName: "Code", key: "code" },
        { headerName: "Name", key: "name" },
        { headerName: "Email", key: "email" },
        { headerName: "Pay Type", key: "pay_type" },
        { headerName: "Employment Status", key: "emp_status" },
        { headerName: "Termination Date", key: "termination_date" },
        { headerName: "", key: "action" },
    ];

    return (
        <div className='content' style={{ padding: "16px 24px 16px 16px" }}>
            <div className="start-new-payroll-section">
                <div>
                    <div className="row mt-4">
                        {userDetail.map((item) => (
                            <Card
                                key={item.title}
                                title={item.title}
                                count={item.count}
                                icon={item.icon}
                                isImage={false}
                                isActive={activeFilter === item.filter && item.filter !== null}
                                onClick={item.filter ? () => handleCardClick(item.filter) : undefined}
                            />
                        ))}
                    </div>
                </div>
                <div>
                    <div className='d-flex justify-content-between mt-5 mb-3' style={{ flexWrap: 'wrap', gap: 8 }}>
                        <div className='d-flex align-items-center gap-3'>
                            <Button startIcon={<img src={PlusIcon} alt="add" />} onClick={() => navigate(stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.ADD_EDIT_EMPLOYEE))}>Add Employees</Button>
                            <button className="btn btn-outline-primary btn-sm" style={{ whiteSpace:'nowrap' }} onClick={() => setShowInviteModal(true)}>
                                ✉ Send Onboarding Invites
                            </button>
                            {!activeFilter && <ToggleSwitch checked={showInactive} onChange={setShowInactive} />}
                        </div>
                        <Input placeholder='Search by Name, Email, or Id' startIcon={search} style={{ width: "100%", maxWidth: "380px" }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                    {activeFilter === 'active' && (
                        <div style={{ display:'flex', alignItems:'center', gap:12, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'8px 14px', marginBottom:12 }}>
                            <span style={{ fontSize:13, color:'#166534', fontWeight:600 }}>
                                Showing {tableData.length} active employee{tableData.length !== 1 ? 's' : ''}
                            </span>
                            <button onClick={() => setActiveFilter(null)} style={{ background:'none', border:'none', color:'#16a34a', fontWeight:700, fontSize:13, cursor:'pointer', padding:0 }}>
                                ✕ Clear filter
                            </button>
                        </div>
                    )}
                    {activeFilter === 'setup_needed' && (
                        <div style={{ display:'flex', alignItems:'center', gap:12, background:'#EFF6FF', border:'1px solid #fed7aa', borderRadius:8, padding:'8px 14px', marginBottom:12 }}>
                            <span style={{ fontSize:13, color:'#c2410c', fontWeight:600 }}>
                                ⚠️ {tableData.length} employee{tableData.length !== 1 ? 's' : ''} need admin setup — open each profile to set wage, pay type, FLSA status, and work location.
                            </span>
                            <button onClick={() => setActiveFilter(null)} style={{ background:'none', border:'none', color:'#1B75F0', fontWeight:700, fontSize:13, cursor:'pointer', padding:0 }}>
                                ✕ Clear filter
                            </button>
                        </div>
                    )}
                    {activeFilter === 'terminated' && (
                        <div style={{ display:'flex', alignItems:'center', gap:12, background:'#EFF6FF', border:'1px solid #fed7aa', borderRadius:8, padding:'8px 14px', marginBottom:12 }}>
                            <span style={{ fontSize:13, color:'#c2410c', fontWeight:600 }}>
                                Showing {tableData.length} terminated employee{tableData.length !== 1 ? 's' : ''}
                            </span>
                            <button onClick={() => setActiveFilter(null)} style={{ background:'none', border:'none', color:'#1B75F0', fontWeight:700, fontSize:13, cursor:'pointer', padding:0 }}>
                                ✕ Clear filter
                            </button>
                        </div>
                    )}
                    {isLoading
                        ? <p className="text-center mt-4">Loading employees...</p>
                        : <Table
                            headers={activeFilter === 'terminated' ? terminatedHeaders : headers}
                            data={tableData}
                            handleView={(d) => navigate(`/employees/view?code=${d.id}`)}
                            handleEdit={(d) => navigate(`/employees/add-edit-employee?id=${d.id}`)}
                          />
                    }
                </div>
            </div>
            {showInviteModal && <SendInviteModal onClose={() => setShowInviteModal(false)} />}
        </div>
    );
}
export default Employees;








